// import React, { useEffect, useMemo, useRef, useCallback } from "react";
import React, { useEffect, useMemo, useRef, useCallback, useSyncExternalStore } from "react";
import { subscribeToolbarEdges, getToolbarEdges } from "./toolbarEdges";
import { useTranslation } from "react-i18next";
import { useSelector, useDispatch } from "react-redux";
import { formatToCustomLocale } from "../../../../utilities";
import { Container, Text } from "@pixi/react";
import { TextStyle } from "pixi.js";
import * as PIXI from "pixi.js";
import anime from "animejs";
import TickerTimeout from "../../../../utils/TickerTimeout";
import StorageManager from "../../../../utils/StorageManager";
import { useDimensions } from "../../../../Contexts/DimensionsContext";
import {
  setCounterAnimationStarted,
  setCounterAnimationFinished,
  updateDisplayedBalance,
  applyReplayWin,
  setPotCollectionOutroCounterFinished,
  clearPotCollectionWin,
  incrementFreeRoundsDisplayedWin,
  clearFreeRoundsWin,
  clearFreeRoundsReloadPending,
  syncFreeRoundsWin,
  applyFreeRoundsSyncedWin,
  syncFreeSpinsDisplayedWin,
  clearFreeSpinsWin,
} from "../../../../State/actions";
import {
  selectCurrentRound,
  selectCurrency,
  selectIsSpinning,
  selectCounterAnimationStarted,
  selectCriticalAnimationPlaying,
  selectFreeRoundsActive,
  selectFreeRoundsCount,
  selectFreeRoundsPerformed,
  selectFreeRoundsDisplayedWin,
  selectFreeRoundsWinCounted,
  selectFreeRoundsEnded,
  selectFreeRoundsReloadPending,
  selectFreeRoundsReloadWinWithheld,
  selectWinAnimationsPlaying,
  selectIsAnimatedWin,
  selectPotCollectionCurrentRound,
  selectPotCollectionActive,
  selectPotCollectionTriggered,
  selectPotCollectionReloadPhase,
  selectShowPotCollectionOutro,
  selectReplaySessionActive,
  selectPotCollectionTriggerRoundWin,
  selectFreeSpinsActive,
  selectFreeSpinsCurrentRound,
  selectFreeSpinsSpinning,
  selectFreeSpinsDisplayedTotalWin,
  selectFreeSpinsTriggered,
} from "../../../../State/selectors";
import {
  selectHoldAndWinActive,
  selectHoldAndWinTriggered,
  selectHoldAndWinCompletedTotalWin,
  clearHoldAndWinWin,
  selectHoldAndWinRoundTotal,
  selectHoldAndWinTotalCounted,
} from "../../../../State/Slices/holdAndWinSlice";
import {
  selectWinAnimationCounterFinished,
  selectPotCollectionOutroCounterFinished,
} from "../../../../State/Slices/animationsSlice";
import {
  FONT_CONFIG,
  USER_INTERFACE_BUTTON_CONFIG,
} from "../../../../pixiSettings";
import { outlineShadow } from "../../../../pixiSettings";
import { PixiSettings } from "../../../../pixiSettings";
import { FEATURES_CONFIG } from "../../../../featuresConfig";
import { moneyDiag } from "../../../../utils/moneyTrace";

/**
 * WinDisplay - Win amount display component for regular gameplay
 *
 * Handles three display modes:
 * 1. Regular win — single line "Win: X" (base game wins)
 * 2. Pot Collection total win — single line "Total Win: X" (after PC outro counter finishes)
 * 3. Free Rounds — up to three lines:
 *    - "Total Win: X" (slides up/down with animation)
 *    - "Win: Y" (current spin win, fixed position)
 *    - "Free Rounds: 3/12" (counter, always fixed at bottom)
 *
 * Total Win slides DOWN into Win's position when no win is showing,
 * and slides UP when a win appears to make room.
 *
 * @param {Object} toolbar - Toolbar reference for positioning
 */
// Clear space kept between the win and the balance / bet text, px.
const PORTRAIT_WIN_CLEARANCE = 10;

const WinDisplay = ({ toolbar }) => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const currentRound = useSelector(selectCurrentRound);
  const currency = useSelector(selectCurrency);
  const isSpinning = useSelector(selectIsSpinning);
  const counterAnimationStarted = useSelector(selectCounterAnimationStarted);
  const criticalAnimationPlaying = useSelector(selectCriticalAnimationPlaying);
  const freeRoundsActive = useSelector(selectFreeRoundsActive);
  const freeRoundsCount = useSelector(selectFreeRoundsCount);
  const freeRoundsPerformed = useSelector(selectFreeRoundsPerformed);
  const freeRoundsDisplayedWin = useSelector(selectFreeRoundsDisplayedWin);
  const freeRoundsWinCounted = useSelector(selectFreeRoundsWinCounted);
  const winAnimationsPlaying = useSelector(selectWinAnimationsPlaying);
  const isAnimatedWin = useSelector(selectIsAnimatedWin);
  const winAnimationCounterFinished = useSelector(
    selectWinAnimationCounterFinished,
  );
  const replaySessionActive = useSelector(selectReplaySessionActive);
  // Replay's stand-in for the localStorage trigger_win it must never read.
  const potCollectionTriggerRoundWin = useSelector(selectPotCollectionTriggerRoundWin);

  // Pot Collection total win state
  const potCollectionCurrentRound = useSelector(selectPotCollectionCurrentRound);
  const potCollectionOutroCounterFinished = useSelector(
    selectPotCollectionOutroCounterFinished,
  );

  // Free Rounds ended flag — true after FR feature completes (shows "Total Win" summary)
  const freeRoundsEnded = useSelector(selectFreeRoundsEnded);

  // Pot Collection active — suppress FR display while bonus is playing
  const potCollectionActive = useSelector(selectPotCollectionActive);
  // Cleopatra's Fire Hold & Win — its own slice, which this display never
  // read: at the first respin the trigger win vanished (the base round's win
  // is cleared on `pending`) and nothing carried it. It now carries the
  // trigger win exactly as Pot Collection does (triggerWinRef below).
  const holdAndWinActive = useSelector(selectHoldAndWinActive);
  // A bonus that owns the Win row with a frozen trigger win as TOTAL WIN.
  const triggerWinBonusActive = potCollectionActive || holdAndWinActive;
  const potCollectionTriggered = useSelector(selectPotCollectionTriggered);
  const potCollectionReloadPhase = useSelector(selectPotCollectionReloadPhase);
  const showPotCollectionOutro = useSelector(selectShowPotCollectionOutro);
  const freeRoundsReloadPending = useSelector(selectFreeRoundsReloadPending);
  const freeRoundsReloadWinWithheld = useSelector(
    selectFreeRoundsReloadWinWithheld,
  );

  // ── Free spins ──
  //
  // The bonus keeps its OWN round and its own spinning flag: spinSlice routes
  // `freespin` away from spin.isSpinning so the two features can never move
  // each other's state. So during the bonus `currentRound` and `isSpinning`
  // above are frozen on the trigger spin — which paid 0 — and every win test
  // built on them reads false. Switching the SOURCE below is what makes this
  // display see the bonus at all.
  const freeSpinsActive = useSelector(selectFreeSpinsActive);
  const freeSpinsRound = useSelector(selectFreeSpinsCurrentRound);
  const freeSpinsSpinning = useSelector(selectFreeSpinsSpinning);
  // NOT freeSpins.totalWin. That one is rolled up the instant the response
  // lands, while the reels are still turning, so showing it would print the
  // win before the player sees it land. This is the presentation-timed figure
  // — the same split freeRounds.displayedWin makes.
  const freeSpinsDisplayedTotalWin = useSelector(
    selectFreeSpinsDisplayedTotalWin,
  );
  // Only to keep the "round is over" mode out of the trigger window — see
  // shouldShowFSCompletedWin. The carried win itself is displayed through
  // displayedTotalWin, which is seeded with it.
  const freeSpinsTriggered = useSelector(selectFreeSpinsTriggered);

  // ── Which round and which "spinning" the win logic reads ──
  //
  // One switch, used everywhere below, so the base game's behaviour is
  // bit-identical when the bonus is not up. Same idea as `displayWin` further
  // down, which already picks between the PC total, the frozen trigger win and
  // the base round.
  const activeRoundWin = freeSpinsActive
    ? (freeSpinsRound?.win ?? 0)
    : (currentRound?.win ?? 0);
  const activeIsSpinning = freeSpinsActive ? freeSpinsSpinning : isSpinning;

  const { device, orientation, getResponsiveConfig } =
    useDimensions();

  // Track previous shouldShowWin state to detect when WinDisplay becomes visible
  const prevShouldShowWinRef = useRef(false);

  // Refs for Total Win slide animation
  const frTotalContainerRef = useRef(null);
  const slideAnimRef = useRef(null);

  // Cached trigger win from localStorage — stable during all respins.
  // Read once when bonus becomes active, cleared when bonus ends.
  // Priority: trigger_win key (set at trigger time, frozen for entire bonus)
  //         → base_game_round.win (fallback before first respin on reload)
  const triggerWinRef = useRef(null);
  // Hold & Win: the base round still holds the trigger win on the frame the
  // room opens (the first respin's `pending` clears it) — taken from there.
  // if (
  //   holdAndWinActive &&
  //   triggerWinRef.current === null &&
  //   currentRound?.triggerWin &&
  //   currentRound.win > 0
  // ) {
  //   triggerWinRef.current = currentRound.win;
  // }
  // ── From a free spin, the trigger is the FREE SPIN's round ── the base
  // round is the spin that entered free spins (its scatter pay), never this
  // bonus's trigger. A free spin that paid nothing has no trigger win, and
  // the stored fallbacks below (base round, trigger_win key) must not stand
  // in for it.
  const holdAndWinTriggerRound = freeSpinsActive ? freeSpinsRound : currentRound;
  if (
    holdAndWinActive &&
    triggerWinRef.current === null &&
    holdAndWinTriggerRound?.triggerWin &&
    holdAndWinTriggerRound.win > 0
  ) {
    triggerWinRef.current = holdAndWinTriggerRound.win;
  }
  if (holdAndWinActive && freeSpinsActive && triggerWinRef.current === null) {
    triggerWinRef.current = 0;
  }
  if (triggerWinBonusActive && triggerWinRef.current === null) {
    if (replaySessionActive) {
      // Replay must not read StorageManager — those keys describe the player's
      // REAL last bonus, so a replayed bonus would show a stranger's trigger
      // win as its Total Win. Replay writes no such keys (spec §9), so the
      // replayed round's own trigger round is the only correct source.
      const replayTriggerWin = potCollectionTriggerRoundWin;
      if (replayTriggerWin > 0) {
        triggerWinRef.current = replayTriggerWin;
      }
    } else {
      const frozenTriggerWin = StorageManager.loadTriggerWin();
      if (frozenTriggerWin > 0) {
        triggerWinRef.current = frozenTriggerWin;
      } else {
        // Fallback — trigger_win key may not exist on reload
        const savedBase = StorageManager.loadBaseGameRound();
        if (savedBase?.round?.triggerWin && savedBase.round.win > 0) {
          triggerWinRef.current = savedBase.round.win;
        }
      }
    }
  }
  if (!triggerWinBonusActive) {
    triggerWinRef.current = null;
  }
  const slideYRef = useRef({ y: 0 }); // Plain object for anime.js to tween
  const prevIsWinModeRef = useRef(false);
  const prevHasFRTotalWinRef = useRef(false);

  // Refs for Win, Total Win, FR Counter containers
  const winTextRef = useRef(null);
  const winShowTimerRef = useRef(null);
  const frCounterRef = useRef(null);

  // Get button dimensions using the same system as PopiClock - memoized
  const buttonConfig = useMemo(
    () => getResponsiveConfig(USER_INTERFACE_BUTTON_CONFIG, true),
    [getResponsiveConfig],
  );

  // ── Toolbar-height-derived sizing system ──────────────────────────────
  // All font sizes, row gaps, and positions are derived from toolbar.height
  // so the 3 FR rows (Total Win, Win, FR Counter) always fit.
  //
  // Portrait modes: toolbar height is 0 (elements float above toolbar).
  // Use visualHeight as the reference in those cases.
  const isPortrait =
    (device === PixiSettings.DEVICE.MOBILE || device === PixiSettings.DEVICE.TABLET) &&
    orientation === PixiSettings.ORIENTATION.PORTRAIT;
  // Where the balance and bet text really end (portrait) — see winX below.
  const edges = useSyncExternalStore(subscribeToolbarEdges, getToolbarEdges);

  const winConfig = useMemo(() => {
    let x = 0;
    let y = 0;

    if (device === PixiSettings.DEVICE.MOBILE) {
      if (orientation === PixiSettings.ORIENTATION.PORTRAIT) {
        x = toolbar.width / 2;
        y = -toolbar.visualHeight / 2;
      } else {
        x = toolbar.width - buttonConfig.width * 8;
        y = toolbar.height / 2;
      }
    } else if (device === PixiSettings.DEVICE.TABLET) {
      if (orientation === PixiSettings.ORIENTATION.PORTRAIT) {
        x = toolbar.width / 2;
        y = -toolbar.visualHeight / 2;
      } else {
        x = toolbar.width / 1.8;
        y = toolbar.height / 2;
      }
    } else {
      // Desktop
      x = toolbar.width / 1.65;
      y = toolbar.height / 2;
    }

    return { x, y };
  }, [
    device,
    orientation,
    toolbar.width,
    toolbar.height,
    toolbar.visualHeight,
    buttonConfig.width,
  ]);

  // Calculate font sizes from available toolbar height.
  // The 3-row FR layout is the tightest constraint — derive from that.
  // Padding fraction: how much vertical space is reserved for top+bottom padding.
  const PADDING_FRACTION = 0.08; // 8% total (4% top + 4% bottom)
  const ROW_COUNT = 3;
  const ROW_GAP_FRACTION = 0.06; // gap between rows as fraction of row height

  const { FONT_SIZE, LABEL_FONT_SIZE, lineHeight } = useMemo(() => {
    // Reference height — portrait uses visualHeight, landscape/desktop uses toolbar height
    const refHeight = isPortrait
      ? (toolbar.visualHeight || buttonConfig.height * 1.7)
      : toolbar.height;

    // Device-specific font scale — mobile portrait text is too large without this
    let fontScale = 1.0;
    if (isPortrait) {
      if (device === PixiSettings.DEVICE.MOBILE) fontScale = 0.75;
      else if (device === PixiSettings.DEVICE.TABLET) fontScale = 0.85;
    }

    const usableHeight = refHeight * fontScale * (1 - PADDING_FRACTION);
    // Each row = rowHeight; gaps between rows = ROW_GAP_FRACTION * rowHeight
    // totalRows * rowHeight + (totalRows - 1) * gap * rowHeight = usableHeight
    // rowHeight * (totalRows + (totalRows - 1) * gap) = usableHeight
    const rowHeight = usableHeight / (ROW_COUNT + (ROW_COUNT - 1) * ROW_GAP_FRACTION);
    const gap = rowHeight * ROW_GAP_FRACTION;
    const lh = rowHeight + gap; // line height = row height + gap

    // Font size: value and label text both fill ~85% of rowHeight
    const valueFontRaw = rowHeight * 0.85;
    const labelFontRaw = rowHeight * 0.85;

    // Snap to nearest even integer for crisp sub-pixel rendering.
    // No lower clamp — matches BalanceDisplay/BetDisplay so the win text keeps
    // shrinking with the viewport (infinite-scaling UI) instead of capping out.
    const fs = Math.round(valueFontRaw / 2) * 2;
    const lfs = Math.round(labelFontRaw / 2) * 2;

    return { FONT_SIZE: fs, LABEL_FONT_SIZE: lfs, lineHeight: lh };
  }, [isPortrait, device, toolbar.height, toolbar.visualHeight, buttonConfig.height]);

  // Ref for freeRoundsActive — used inside spin reset effect without adding it
  // as a dependency (which would cause re-fires when FR status changes mid-spin).
  const freeRoundsActiveRef = useRef(freeRoundsActive);
  freeRoundsActiveRef.current = freeRoundsActive;

  // Same reason as the ref above: read inside the spin reset without making
  // the effect re-fire when the bonus ends.
  const freeSpinsActiveRef = useRef(freeSpinsActive);
  freeSpinsActiveRef.current = freeSpinsActive;
  const holdAndWinActiveRef = useRef(holdAndWinActive);
  holdAndWinActiveRef.current = holdAndWinActive;

  // Ref for freeRoundsDisplayedWin — used inside effects for localStorage persistence
  // without adding it as a dependency (would re-fire effects on every value change).
  const freeRoundsDisplayedWinRef = useRef(freeRoundsDisplayedWin);
  freeRoundsDisplayedWinRef.current = freeRoundsDisplayedWin;

  // Reset component state when new spin begins.
  //
  // Keyed off activeIsSpinning, so a free spin resets the counter flags and
  // prevShouldShowWin exactly as a base spin does. Without it the transition
  // detector below stays latched after the first bonus win and never fires
  // again — no total-win advance, no balance update, for the rest of the round.
  useEffect(() => {
    if (activeIsSpinning) {
      dispatch(setCounterAnimationStarted(false));
      dispatch(setCounterAnimationFinished(false));
      dispatch(setPotCollectionOutroCounterFinished(false));
      dispatch(clearPotCollectionWin()); // Clear preserved PC win data
      // The finished Hold & Win's total, the same moment (its twin).
      if (!holdAndWinActiveRef.current) dispatch(clearHoldAndWinWin());
      // Only clear FR win data after FR has ended (post-FR "Total Win" cleanup).
      // During active FR, displayedWin must persist across spins — don't zero it.
      // The preserved free-spins total, cleared on the next BASE spin only —
      // freeSpinsActive is false by then, so this cannot fire between free
      // spins. Same moment clearPotCollectionWin above is dispatched.
      if (!freeSpinsActiveRef.current) {
        dispatch(clearFreeSpinsWin());
      }
      if (!freeRoundsActiveRef.current) {
        dispatch(clearFreeRoundsWin());
      } else {
        // Clear reload flag on first spin — reload is over once the player spins
        dispatch(clearFreeRoundsReloadPending());
      }
      prevShouldShowWinRef.current = false;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeIsSpinning, dispatch]);

  // Reset PC outro counter flag when a new bonus starts.
  // During PC, Redux isSpinning is never set, so the reset at line 246 never fires.
  // Without this, a stale potCollectionOutroCounterFinished=true from a previous bonus
  // causes isPCMode to fire immediately when pcTotalWin updates from the respin response,
  // showing the total win before the max win dialog counter finishes.
  useEffect(() => {
    if (potCollectionActive) {
      dispatch(setPotCollectionOutroCounterFinished(false));
    }
  }, [potCollectionActive, dispatch]);

  // Cleanup slide animation on unmount
  useEffect(() => {
    return () => {
      if (slideAnimRef.current) {
        slideAnimRef.current.pause();
        slideAnimRef.current = null;
      }
    };
  }, []);

  // ── Pot Collection total win ──────────────────────────────────────────
  // const pcTotalWin = potCollectionCurrentRound?.win || 0;
  const potCollectionTotalWin = potCollectionCurrentRound?.win || 0;
  // const shouldShowPCTotalWin = useMemo(
  //   () => potCollectionOutroCounterFinished && pcTotalWin > 0 && !activeIsSpinning,
  //   [potCollectionOutroCounterFinished, pcTotalWin, activeIsSpinning],
  // );
  const shouldShowPotCollectionTotalWin =
    potCollectionOutroCounterFinished && potCollectionTotalWin > 0 && !activeIsSpinning;
  // ── Cleopatra's Hold & Win, done: Pot Collection's slot ── the two never
  // run in one game, and they end the same way — the round's TOTAL WIN stays
  // on the base game's win display until the next spin (holdAndWin
  // completedTotalWin, kept through resetHoldAndWin). Vladimir, 2026-09-30:
  // "user must see what he has won".
  const holdAndWinTriggered = useSelector(selectHoldAndWinTriggered);
  const holdAndWinCompletedTotalWin = useSelector(selectHoldAndWinCompletedTotalWin);
  // const shouldShowHoldAndWinTotalWin =
  //   !holdAndWinActive &&
  //   !holdAndWinTriggered &&
  //   holdAndWinCompletedTotalWin > 0 &&
  //   !activeIsSpinning;
  // Back in FREE SPINS the bonus total is not a "round over" Total Win — the
  // free spins keep their rows; it shows as the WIN (fsHoldAndWinWin below).
  const shouldShowHoldAndWinTotalWin =
    !holdAndWinActive &&
    !holdAndWinTriggered &&
    !freeSpinsActive &&
    holdAndWinCompletedTotalWin > 0 &&
    !activeIsSpinning;
  // ── A Hold & Win won on a free spin, its Total Win counted ── still in the
  // room: the win display writes what the bonus paid — the popup's figure —
  // as the Total Win, in place of the trigger win (Vladimir, 2026-10-06).
  // Back in free spins the same amount becomes the WIN (fsHoldAndWinWin).
  // const holdAndWinPaid = useSelector(selectHoldAndWinWin);
  // The bonus's whole total — trigger win included, as the popup counts it.
  const holdAndWinPaid = useSelector(selectHoldAndWinRoundTotal);
  const holdAndWinTotalCounted = useSelector(selectHoldAndWinTotalCounted);
  // ── ...and won on a BASE spin too (Vladimir, 2026-10-08) ── Pot
  // Collection's rule: its Total Win goes up the moment the outro popup has
  // counted (potCollectionOutroCounterFinished), still in the room. The H&W
  // popup raises totalCounted for both rooms; only free spins read it.
  // const fsHoldAndWinCounted =
  //   !!freeSpinsActive &&
  //   !!holdAndWinActive &&
  const fsHoldAndWinCounted =
    !!holdAndWinActive &&
    !!holdAndWinTotalCounted &&
    holdAndWinPaid > 0;
  // ── A Hold & Win won on a free spin, handed back ── its total (trigger win
  // + what it paid) is the WIN row, and the free spins' Total Win row above it
  // already holds it (TransitionDialog's exit added the payout) — the
  // free-rounds rule. Until the next free spin starts.
  const fsHoldAndWinWin =
    !!freeSpinsActive &&
    !holdAndWinActive &&
    !holdAndWinTriggered &&
    holdAndWinCompletedTotalWin > 0 &&
    !activeIsSpinning;
  // The finished bonus's total — whichever of the two this game has.
  const pcTotalWin = shouldShowPotCollectionTotalWin
    ? potCollectionTotalWin
    : holdAndWinCompletedTotalWin;
  const shouldShowPCTotalWin =
    shouldShowPotCollectionTotalWin || shouldShowHoldAndWinTotalWin;

  // ── Free spins completed total win (same pattern as PC and FR) ──────────
  //
  // The round is over, the wipe has carried the player back, and the Total Win
  // must STAY on screen until they spin the base game again. Pot Collection
  // does this by preserving its currentRound through resetPotCollection and
  // clearing it on the next spin; free rounds does it with displayedWin. Ours
  // preserves the two totals through resetFreeSpins, so the row simply keeps
  // reading them.
  // `!freeSpinsTriggered` because the total is now SEEDED with the trigger win
  // at trigger time, so without it this "the round is over" mode would fire in
  // the window before the round has even opened.
  const shouldShowFSCompletedWin = useMemo(
    () =>
      !freeSpinsActive &&
      !freeSpinsTriggered &&
      freeSpinsDisplayedTotalWin > 0 &&
      !activeIsSpinning,
    [freeSpinsActive, freeSpinsTriggered, freeSpinsDisplayedTotalWin, activeIsSpinning],
  );

  // ── Free Rounds completed total win (same pattern as PC) ──────────────────
  const shouldShowFRCompletedWin = useMemo(
    () => freeRoundsEnded && freeRoundsDisplayedWin > 0 && !activeIsSpinning,
    [freeRoundsEnded, freeRoundsDisplayedWin, activeIsSpinning],
  );

  // ── Regular win display ───────────────────────────────────────────────
  const rawShouldShowWin = useMemo(() => {
      // Win celebration (Big/Mega/SuperMega): show as soon as counter finishes
      const celebrationPath = winAnimationCounterFinished && activeRoundWin > 0 && !activeIsSpinning;
      // Regular win (no animated win)
      const regularPath =
        counterAnimationStarted &&
        activeRoundWin > 0 &&
        !activeIsSpinning &&
        !criticalAnimationPlaying &&
        (!isAnimatedWin || !winAnimationsPlaying);

      const result = celebrationPath || regularPath;

      return result;
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      counterAnimationStarted,
      activeRoundWin,
      activeIsSpinning,
      criticalAnimationPlaying,
      winAnimationCounterFinished,
      isAnimatedWin,
      winAnimationsPlaying,
    ],
  );

  // ── Once the win is on screen, it STAYS there for the round ──
  //
  // `criticalAnimationPlaying` above is the right gate for showing a win — it
  // keeps the row down while a feature owns the screen — but a poor one for
  // taking it away again, because it is raised LATER in the same round.
  //
  // A free-spins trigger does exactly that: the scatter line starts SC1's
  // reveal and useWinController raises the flag for it (the strip has to spin
  // with the UI locked). The player had a paid win on screen, and it vanished
  // the moment the scatters lit — mid-round, with nothing to explain it. Pot
  // Collection never showed the bug because its trigger line runs no reveal, so
  // the flag is never raised while its win is up.
  //
  // Latched rather than gated differently, so the SHOW rules are untouched:
  // nothing new can make the row appear, only stay. It is released the moment
  // the round is: `activeIsSpinning` covers a base spin and a free spin alike,
  // and `activeRoundWin > 0` drops it as soon as the round being displayed
  // switches to one that has not paid — which is how the row hands over to
  // Total Win on entering the bonus.
  //
  // Mutating a ref during render, the same way triggerWinRef above does it, and
  // for the same reason: this is derived state, not an effect, and running it a
  // frame later would show the retraction the latch exists to prevent.
  const winLatchedRef = useRef(false);
  if (activeIsSpinning || activeRoundWin <= 0) {
    winLatchedRef.current = false;
  } else if (rawShouldShowWin) {
    winLatchedRef.current = true;
  }
  const shouldShowWin = rawShouldShowWin || winLatchedRef.current;

  // ── Determine modes ───────────────────────────────────────────────────
  // Defensive !! coercion — Pixi's React reconciler throws on bare 0/number children,
  // so all flags used in JSX {flag && <Component/>} guards must be proper booleans.
  // Trigger win "Total Win" shown during active bonus ONLY when a cached trigger win exists.
  // Without trigger win, nothing shows until the outro counter finishes.
  // Suppress during "collection" reload — bonus is done, max win/outro popup hasn't appeared yet.
  // Suppress when max win popup is active — total win must wait for the dialog's reveal.
  const isBonusTriggerWinMode = !!(triggerWinBonusActive && triggerWinRef.current > 0);
  // ── Free spins does NOT get a trigger-win mode of its own ──
  //
  // Note the gate above: potCollectionACTIVE. Pot Collection shows the carried
  // win once the feature owns the screen, never during the trigger. Ours works
  // the same way and needs no mode for it, because freeSpins.displayedTotalWin
  // is now SEEDED with the trigger win — so the moment the round opens,
  // isFreeSpinsMode's Total Win row is already showing it.
  //
  // There was a mode here that fired on `triggered && !active`. That is the
  // trigger window, where the base win is still being presented as a WIN and
  // the feature has not been entered — relabelling it "Total Win" there is
  // wrong twice over, and it also put the row up underneath the reveal.
  const isPCMode = !!shouldShowPCTotalWin && (!freeRoundsActive || potCollectionActive);
  // ── Back from a bonus INSIDE a free-rounds campaign ──
  //
  // Pot Collection's rule, ported: its "bonus done" mode is switched off during
  // a campaign (`isPCMode` above, `!freeRoundsActive || potCollectionActive`),
  // so the free-rounds rows own the display and the Total Win row shows the
  // campaign total — which the exit transition has just updated with the whole
  // bonus (TransitionDialog, the free-rounds branch). Free spins had no such
  // guard: its done mode took the Total Win row and showed the BONUS total,
  // while the campaign total (86 -> 769 on the 2026-09-24 trace) was updated
  // underneath and never reached the screen.
  //
  // Plus what Vladimir asked for on top: the bonus total is shown as the WIN,
  // so the player sees what the bonus paid AND the campaign total it went
  // into. *"we have to see win that is won ... written in win display, and
  // total win updated."* Held for the same window the done mode uses — until
  // the next spin.
  const fsWinInCampaign =
    !!FEATURES_CONFIG.FREE_ROUNDS.ENABLED &&
    freeRoundsActive &&
    !isPCMode &&
    !!shouldShowFSCompletedWin;
  // ── The same for Cleopatra's Hold & Win (2026-10-07) ── back in the base
  // game inside a campaign, the bonus "done" mode is off (isPCMode), the
  // free-rounds rows show the campaign total (the exit updated it) and the
  // bonus's total is the WIN — until the next spin.
  const hnwWinInCampaign =
    !!FEATURES_CONFIG.FREE_ROUNDS.ENABLED &&
    freeRoundsActive &&
    !isPCMode &&
    !!shouldShowHoldAndWinTotalWin;
  const isFSCompletedMode =
    !isPCMode && !!shouldShowFSCompletedWin && !fsWinInCampaign;
  const isFRCompletedMode =
    !isPCMode && !isFSCompletedMode && !!shouldShowFRCompletedWin;
  const isWinMode =
    !isPCMode &&
    !isFRCompletedMode &&
    // (!!shouldShowWin || isBonusTriggerWinMode || fsWinInCampaign);
    // (!!shouldShowWin || isBonusTriggerWinMode || fsWinInCampaign || fsHoldAndWinWin);
    (!!shouldShowWin ||
      isBonusTriggerWinMode ||
      fsWinInCampaign ||
      hnwWinInCampaign ||
      fsHoldAndWinWin ||
      fsHoldAndWinCounted);
  // `&& !freeSpinsActive` for the same reason `&& !potCollectionActive` is
  // already there: a bonus can be won inside a free round, and only one of them
  // may own the rows. The feature on screen wins.
  // const isFreeRoundsMode = !!(FEATURES_CONFIG.FREE_ROUNDS.ENABLED && freeRoundsActive && !potCollectionActive && !freeSpinsActive);
  // And not while Cleopatra's Hold & Win owns the screen (2026-10-07), as Pot
  // Collection: its trigger-win Total Win row is up there.
  const isFreeRoundsMode = !!(FEATURES_CONFIG.FREE_ROUNDS.ENABLED && freeRoundsActive && !potCollectionActive && !freeSpinsActive && !holdAndWinActive);
  // Free spins: the Free Rounds shape MINUS the counter row — the spins
  // remaining are already on the play button, so a third row would duplicate
  // them. Two rows means the existing slide animation covers it unchanged.
  // const isFreeSpinsMode = !isPCMode && !!freeSpinsActive;
  // Not while a Hold & Win won here owns the screen: the free spins' Total
  // Win row goes with the room and comes back, updated, at the exit.
  const isFreeSpinsMode = !isPCMode && !!freeSpinsActive && !holdAndWinActive;

  // Which feature owns the Total Win row, and what it reads.
  const hasTotalWinRow =
    isFreeSpinsMode || isFSCompletedMode
      ? freeSpinsDisplayedTotalWin > 0
      : (isFreeRoundsMode || isFRCompletedMode) && freeRoundsDisplayedWin > 0;
  const totalWinRowValue =
    isFreeSpinsMode || isFSCompletedMode
      ? freeSpinsDisplayedTotalWin
      : freeRoundsDisplayedWin;



  // ── Free Rounds: total win update when a win is shown ─────────────────
  // Regular wins: manual increment (fast, no network call needed).
  // Animated wins (Big Win etc.): pre-fetch from server when animation starts
  // (hides latency behind counter time), apply when counter finishes.
  // Skip when bonus is active — bonus wins are added after outro counter finishes.
  // Also skip when outro counter just finished — the transition back to base game
  // re-exposes the trigger round's win, which was already counted before bonus started.

  // Pre-fetch: fire sync when animated win starts during FR (skip last round — FR is
  // already closed on server, sync would return nothing)
  const isLastFRRound = freeRoundsPerformed >= freeRoundsCount && freeRoundsCount > 0;
  const syncFiredRef = useRef(false);
  useEffect(() => {
    // Replay never pre-fetches: syncFreeRoundsWin hits the live server for the
    // player's CURRENT free-rounds total, which has nothing to do with the
    // recorded round being replayed (and on a standalone replay link there's
    // no API to call at all). The manual-increment branch below covers replay
    // instead — it derives the same number from the replayed round itself.
    if (replaySessionActive) return;
    // if (winAnimationsPlaying && isAnimatedWin && freeRoundsActive && !potCollectionActive && !freeSpinsActive && !freeSpinsTriggered && !isLastFRRound) {
    // Not on a Hold & Win trigger either (2026-10-07) — its win reaches the
    // campaign at the bonus's exit (TransitionDialog), as free spins' does.
    if (winAnimationsPlaying && isAnimatedWin && freeRoundsActive && !potCollectionActive && !freeSpinsActive && !freeSpinsTriggered && !holdAndWinTriggered && !holdAndWinActive && !isLastFRRound) {
      if (!syncFiredRef.current) {
        syncFiredRef.current = true;
        dispatch(syncFreeRoundsWin());
      }
    }
    // Reset when animations stop — ready for next win
    if (!winAnimationsPlaying) {
      syncFiredRef.current = false;
    }
  }, [winAnimationsPlaying, isAnimatedWin, freeRoundsActive, potCollectionActive, freeSpinsActive, freeSpinsTriggered, holdAndWinTriggered, holdAndWinActive, isLastFRRound, replaySessionActive, dispatch]);

  // Apply: when counter finishes and win is shown
  useEffect(() => {
    // ── Not inside the free-spins room either ──
    //
    // `!potCollectionActive` for the same reason: a bonus inside a campaign
    // reaches the Total Win ONCE, at its exit — see the free-rounds branch of
    // TransitionDialog's free-spins exit. In the room this effect fired on every
    // paying free spin and added `currentRound.win`, which is the BASE round —
    // the trigger spin — so a trigger that paid was counted again on each one.
    //
    // ── ...and not on the TRIGGER spin itself ──
    //
    // `!potCollectionTriggered`, ported. A win that triggers free spins is
    // carried INTO the bonus — freeSpinsSlice seeds `totalWin` with it — and
    // reaches the campaign's Total Win with the rest of the room at the exit.
    // Counted here as well, it went in immediately and then came back out when
    // `collect_freespins` resynced the server's figure (86 -> 99 -> 86 on the
    // 2026-09-24 trace). Vladimir: *"we do not add that to total win
    // immediately, we transfer that as a total win to free spins."*
    // ── ...and not for Cleopatra's Hold & Win (2026-10-07) ── the same three
    // guards as Pot Collection: not in the room (`holdAndWinActive`), not on the
    // trigger spin (`holdAndWinTriggered` — the win is carried into the bonus,
    // its Total Win counts it), and not after the exit while the bonus total is
    // still up (`holdAndWinCompletedTotalWin`, as `potCollectionOutroCounterFinished`):
    // the exit has added the whole bonus to the campaign (TransitionDialog).
    // if (shouldShowWin && !prevShouldShowWinRef.current && freeRoundsActive && !potCollectionActive && !freeSpinsActive && !freeSpinsTriggered && !potCollectionTriggered && !potCollectionOutroCounterFinished) {
    if (shouldShowWin && !prevShouldShowWinRef.current && freeRoundsActive && !potCollectionActive && !freeSpinsActive && !freeSpinsTriggered && !potCollectionTriggered && !potCollectionOutroCounterFinished && !holdAndWinActive && !holdAndWinTriggered && !(holdAndWinCompletedTotalWin > 0)) {
      const winAmount = currentRound?.win ?? 0;

      if (winAmount > 0) {
        if (isAnimatedWin && !isLastFRRound && !replaySessionActive) {
          // Animated win (not last round): apply pre-fetched server value.
          // Never in replay — nothing was pre-fetched (see the sync effect
          // above), so this would apply a stale/zero value.
          dispatch(applyFreeRoundsSyncedWin());
        } else if (
          (!freeRoundsReloadPending || freeRoundsReloadWinWithheld) &&
          !freeRoundsWinCounted
        ) {
          // Regular win OR last round animated win: manual increment
          // Skip on reload — displayedWin already has this win from server init
          //
          // ── And skip a round whose money is ALREADY in the total ──
          //
          // Leaving a replay re-presents the restored round on purpose: the
          // player gets their lines, boxes and money back. `winAlreadyPresented`
          // withholds only the CELEBRATION, and it does that by forcing
          // `isAnimatedWin` false — which is exactly what drops the round out of
          // the branch above and into THIS one. So a free-rounds win that was
          // paid once, watched, and then replayed came back through the manual
          // increment and was added a SECOND time. Once per replay.
          //
          // The snapshot does not save us: `displayedWin` IS restored by
          // endReplaySession, and this fires after that restore, on the
          // re-presentation it triggers.
          //
          // ── Keyed on `winCounted`, not on `winAlreadyPresented` ──
          //
          // That flag means "the celebration was shown", which is a proxy for
          // "the money was counted" and not the same thing. It is stamped
          // unconditionally on whatever round is current when a session ends, so
          // a player who opened History while the win counter was still running
          // — before the increment had fired — would come back to a round marked
          // presented whose money had never been added, and the guard would
          // swallow the one increment that owed it. The total would sit SHORT
          // until the next spin resynced from the server.
          dispatch(incrementFreeRoundsDisplayedWin(winAmount));
          // On last round, server won't have this win on next init — persist for reload.
          // Replay must never write FR state to storage (spec §9) — its
          // numbers are the recorded round's, not the player's real session.
          if (isLastFRRound && !replaySessionActive) {
            const newTotal = (freeRoundsDisplayedWinRef.current || 0) + winAmount;
            StorageManager.updateFreeRoundsWin(newTotal);
          }
        }
      }
    } else if (shouldShowWin && !prevShouldShowWinRef.current && freeRoundsActive) {

    }
  }, [shouldShowWin, freeRoundsActive, potCollectionActive, freeSpinsActive, freeSpinsTriggered, potCollectionTriggered, potCollectionOutroCounterFinished, holdAndWinActive, holdAndWinTriggered, holdAndWinCompletedTotalWin, isAnimatedWin, isLastFRRound, freeRoundsReloadPending, freeRoundsReloadWinWithheld, currentRound?.win, freeRoundsWinCounted, replaySessionActive, dispatch]);

  // ── Free Rounds + Bonus: sync FR total win from server ──
  // Pre-fetch: fire sync when outro popup starts during FR (hides network latency)
  // Same pattern as animated win sync (lines 318–322)
  const pcOutroSyncFiredRef = useRef(false);
  const wasReloadBonusRef = useRef(false);
  useEffect(() => {
    // Snapshot reload state when a new bonus starts
    if (potCollectionActive && !potCollectionOutroCounterFinished) {
      pcOutroSyncFiredRef.current = false;
      wasReloadBonusRef.current = potCollectionReloadPhase !== "none";
    }
    // Fire sync when outro popup shows during FR (natural play only).
    // Never in replay — same reason as the animated-win sync above: it would
    // fetch the player's live FR total instead of the replayed round's.
    if (
      showPotCollectionOutro &&
      freeRoundsActive &&
      !pcOutroSyncFiredRef.current &&
      !wasReloadBonusRef.current &&
      !replaySessionActive
    ) {
      pcOutroSyncFiredRef.current = true;

      dispatch(syncFreeRoundsWin());
    }
  }, [showPotCollectionOutro, potCollectionActive, potCollectionOutroCounterFinished, freeRoundsActive, potCollectionReloadPhase, replaySessionActive, dispatch]);

  // Apply: when outro counter finishes during FR, update FR total win
  const pcOutroAppliedRef = useRef(false);
  useEffect(() => {
    if (potCollectionActive && !potCollectionOutroCounterFinished) {
      pcOutroAppliedRef.current = false;
    }

    // wasReloadBonusRef is true for ANY reload-driven replay of this bonus —
    // that's the right guard for the non-last-round sync path (don't re-apply
    // a value that was already synced). But for the LAST round, a reload can
    // replay this exact reveal for the very FIRST time (user reloaded before
    // it ever played live) — in that case the win was never actually folded
    // into the persisted total, and skipping would silently drop it forever.
    // lastRoundApplied (see StorageManager) distinguishes the two: only skip
    // when the total was already corrected by a completed reveal.
    // Replay never touches the real FR storage record (spec §9) — reading it
    // would judge the replayed round by the player's live campaign, writing it
    // would corrupt that campaign's persisted total.
    const savedFR =
      isLastFRRound && !replaySessionActive
        ? StorageManager.loadFreeRoundsRound()
        : null;
    const mustApplyDespiteReload = isLastFRRound && savedFR?.lastRoundApplied === false;
    const blockedByReload = wasReloadBonusRef.current && !mustApplyDespiteReload;

    if (
      potCollectionOutroCounterFinished &&
      freeRoundsActive &&
      !pcOutroAppliedRef.current &&
      !blockedByReload
    ) {
      pcOutroAppliedRef.current = true;
      // Replay always takes the manual branch, not just on the last round:
      // the pre-fetch it would otherwise apply is deliberately never fired
      // during replay (see the sync effect above), so applyFreeRoundsSyncedWin
      // would be a silent no-op and a bonus won inside a mid-campaign free
      // round would never reach the Total Win. Replay doesn't need the server
      // here anyway — pcTotalWin is the replayed bonus's own total.
      if (isLastFRRound || replaySessionActive) {
        // Last FR round: server already closed FR, sync returns stale data.
        // Manually add bonus total win and persist for completion popup.

        dispatch(incrementFreeRoundsDisplayedWin(pcTotalWin));
        if (!replaySessionActive) {
          const newTotal = (freeRoundsDisplayedWinRef.current || 0) + pcTotalWin;
          StorageManager.updateFreeRoundsWin(newTotal);
        }
      } else {
        // Normal FR round: apply pre-fetched server value

        dispatch(applyFreeRoundsSyncedWin());
      }
    }
  }, [potCollectionOutroCounterFinished, potCollectionActive, freeRoundsActive, isLastFRRound, pcTotalWin, replaySessionActive, dispatch]);

  // Balance update for regular wins
  useEffect(() => {
    if (shouldShowWin && !prevShouldShowWinRef.current) {
      // ── Free spins: advance the Total Win row now ──
      //
      // Exactly where Free Rounds increments its own displayed total, on the
      // same false->true transition. The difference is that ours SNAPS to the
      // real total instead of adding to it: freeSpins.totalWin is already
      // authoritative, so there is nothing to accumulate and nothing to drift.
      //
      // That is also why this needs no reload guard. FR has to check
      // freeRoundsReloadPending before incrementing, because a reload would
      // otherwise count the same win twice; an assignment is idempotent, so a
      // reload that re-presents a restored win simply re-snaps to the value it
      // already had.
      // if (isFreeSpinsMode) {
      //   dispatch(syncFreeSpinsDisplayedWin());
      // }
      // ── Not on a free spin that triggered Hold & Win ── its win belongs to
      // the bonus: it reaches the free spins' Total Win together with what
      // the bonus pays, at the exit (TransitionDialog) — not now (Vladimir,
      // 2026-10-06). freeSpins.totalWin already holds it; only the DISPLAYED
      // total waits.
      if (isFreeSpinsMode && !holdAndWinTriggered) {
        dispatch(syncFreeSpinsDisplayedWin());
      }

      // ── Bonus rounds never move the balance mid-feature ──
      //
      // Every bonus in this game works the same way: the TOTAL WIN row counts
      // up on each win, and the balance is left alone until the feature is over
      // — the total win dialog closes, the wipe carries the player back, and the
      // balance is synced once on the far side. Pot Collection does exactly
      // this, and free rounds are already in the list below for the same
      // reason. See the leavingFreeSpins branch in TransitionDialog.
      // const shouldSkipBalanceUpdate =
      //   freeRoundsActive ||
      //   isFreeSpinsMode ||
      //   winAnimationCounterFinished ||
      //   isAnimatedWin;
      // ── ...and not on the spin that TRIGGERS one (QA, 2026-10-28) ──
      // Its win goes in with the bonus's winnings at the exit. Live, the
      // adapter already leaves it out of the balance; a replay would credit
      // it here, and its exit (TransitionDialog) only counts what is not in yet.
      const shouldSkipBalanceUpdate =
        freeRoundsActive ||
        isFreeSpinsMode ||
        freeSpinsTriggered ||
        holdAndWinTriggered ||
        winAnimationCounterFinished ||
        isAnimatedWin;

      if (!shouldSkipBalanceUpdate) {
        if (replaySessionActive) {
          // Replay (both entry points): never touch the live wallet
          // (updateDisplayedBalance reads the real Redux balance) — add
          // this step's win to the replay-only display balance instead.
          // Same trigger point as live, just a different destination — see
          // replaySlice.applyReplayWin, which fetchReplayInit resets on
          // every new playthrough so each replay reveals the win again
          // from the round's own starting balance.
          dispatch(applyReplayWin(currentRound?.win || 0));
        } else {
          dispatch(updateDisplayedBalance());
        }
      }
    }

    prevShouldShowWinRef.current = shouldShowWin;
  }, [
    shouldShowWin,
    dispatch,
    freeRoundsActive,
    winAnimationCounterFinished,
    isAnimatedWin,
    replaySessionActive,
    currentRound?.win,
    isFreeSpinsMode,
    holdAndWinTriggered,
    freeSpinsTriggered,
  ]);

  // ── Total Win slide animation ─────────────────────────────────────────
  // When win appears → slide Total Win UP (from win position to top position)
  // When win disappears → slide Total Win DOWN (from top back to win position)
  //
  // Fixed positions (relative to container center y=0):
  //   - Top row (Total Win when win is showing): -lineHeight / 2
  //   - Win row (Total Win when no win / Win when showing): 0 (center, shifted up by half counter)
  //   - FR counter row: always at +lineHeight (below)
  //
  // When win is NOT showing, Total Win sits at the win row (0).
  // When win IS showing, Total Win slides up to the top row (-lineHeight).

  // Y positions for the Total Win container
  const frTotalYWhenAlone = 0;             // center — where "Win" normally sits
  const frTotalYWhenWinShowing = -lineHeight;  // slide up to make room

  useEffect(() => {
    if (!hasTotalWinRow || !frTotalContainerRef.current) {
      prevHasFRTotalWinRef.current = hasTotalWinRow;
      return;
    }

    const wasVisible = prevHasFRTotalWinRef.current;
    const wasWinMode = prevIsWinModeRef.current;
    prevHasFRTotalWinRef.current = hasTotalWinRow;
    prevIsWinModeRef.current = isWinMode;

    // Total Win just appeared — snap to correct position, no animation
    if (!wasVisible) {
      const snapY = isWinMode ? frTotalYWhenWinShowing : frTotalYWhenAlone;
      slideYRef.current.y = snapY;
      frTotalContainerRef.current.y = snapY;
      return;
    }

    // Only animate on actual isWinMode transitions
    if (isWinMode === wasWinMode) return;

    // Cancel any running animation
    if (slideAnimRef.current) {
      slideAnimRef.current.pause();
      slideAnimRef.current = null;
    }

    const targetY = isWinMode ? frTotalYWhenWinShowing : frTotalYWhenAlone;
    const slideDelay = isWinMode ? 0 : 35; // wait for win text to hide before sliding down

    slideAnimRef.current = anime({
      targets: slideYRef.current,
      y: targetY,
      duration: 100,
      delay: slideDelay,
      easing: "easeOutCubic",
      update: () => {
        if (frTotalContainerRef.current) {
          frTotalContainerRef.current.y = slideYRef.current.y;
        }
      },
    });
  }, [isWinMode, hasTotalWinRow, frTotalYWhenWinShowing, frTotalYWhenAlone]);

  // Re-snap animated container positions on resize/rotation ONLY.
  // Track lineHeight via ref — only snap when it actually changes (resize).
  // isWinMode transitions are handled by the slide animation above.
  const prevLineHeightRef = useRef(lineHeight);
  useEffect(() => {
    if (prevLineHeightRef.current === lineHeight) return;
    prevLineHeightRef.current = lineHeight;
    if (!frTotalContainerRef.current) return;
    // Cancel any running animation so it doesn't fight us
    if (slideAnimRef.current) {
      slideAnimRef.current.pause();
      slideAnimRef.current = null;
    }
    const correctY = isWinMode ? frTotalYWhenWinShowing : frTotalYWhenAlone;
    slideYRef.current.y = correctY;
    frTotalContainerRef.current.y = correctY;
  }, [lineHeight, isWinMode, frTotalYWhenWinShowing, frTotalYWhenAlone]);

  // Whether the win text section should be visible
  const winTextShouldShow = isPCMode || isWinMode;

  // Ref callbacks: set initial alpha=0 on mount
  const setWinTextRef = useCallback((node) => {
    winTextRef.current = node;
    if (node) node.alpha = 0;
  }, []);

  const setFRCounterRef = useCallback((node) => {
    frCounterRef.current = node;
    if (node) node.alpha = 0;
  }, []);

  // Instant show/hide via ref alpha
  // When showing during FR with Total Win visible, delay 35ms so Total Win slides up first
  useEffect(() => {
    const node = winTextRef.current;
    if (!node) return;

    // Clear any pending delayed show
    if (winShowTimerRef.current) {
      winShowTimerRef.current.clear();
      winShowTimerRef.current = null;
    }

    if (winTextShouldShow) {
      if (hasTotalWinRow) {
        // Delay so Total Win slides up before win text appears
        winShowTimerRef.current = new TickerTimeout(() => {
          node.alpha = 1;
          winShowTimerRef.current = null;
        }, 35);
      } else {
        node.alpha = 1;
      }
    } else {
      node.alpha = 0;
    }

    return () => {
      if (winShowTimerRef.current) {
        winShowTimerRef.current.clear();
        winShowTimerRef.current = null;
      }
    };
  }, [winTextShouldShow, hasTotalWinRow]);

  useEffect(() => {
    const node = frTotalContainerRef.current;
    if (node) node.alpha = hasTotalWinRow ? 1 : 0;
  }, [hasTotalWinRow]);

  useEffect(() => {
    const node = frCounterRef.current;
    if (node) node.alpha = isFreeRoundsMode ? 1 : 0;
  }, [isFreeRoundsMode]);

  // Text style objects - memoized
  const winLabelStyle = useMemo(
    () =>
      new TextStyle({
        fontFamily: FONT_CONFIG.PRIMARY,
        fontSize: LABEL_FONT_SIZE,
        fontWeight: "400",
        fill: "#ffffff",
        align: "center",
        resolution: window.devicePixelRatio || 1,
        // ...TEXT_STYLE_CONFIG.OUTLINE,
        ...outlineShadow(LABEL_FONT_SIZE), // shadow scales with the text
      }),
    [LABEL_FONT_SIZE],
  );

  const winStyle = useMemo(
    () =>
      new TextStyle({
        fontFamily: FONT_CONFIG.PRIMARY,
        fontSize: FONT_SIZE,
        fontWeight: "700",
        fill: "#ffbf11",
        align: "center",
        resolution: window.devicePixelRatio || 1,
        // ...TEXT_STYLE_CONFIG.OUTLINE,
        ...outlineShadow(FONT_SIZE), // shadow scales with the text
      }),
    [FONT_SIZE],
  );

  // Free Rounds counter label style — slightly smaller than win label
  const FR_LABEL_FONT_SIZE = Math.round((LABEL_FONT_SIZE * 0.85) / 2) * 2;

  const frLabelStyle = useMemo(
    () =>
      new TextStyle({
        fontFamily: FONT_CONFIG.PRIMARY,
        fontSize: FR_LABEL_FONT_SIZE,
        fontWeight: "400",
        fill: "#ffffff",
        align: "center",
        resolution: window.devicePixelRatio || 1,
        // ...TEXT_STYLE_CONFIG.OUTLINE,
        ...outlineShadow(FR_LABEL_FONT_SIZE), // shadow scales with the text
      }),
    [FR_LABEL_FONT_SIZE],
  );

  // Ref callback to set initial position and alpha when Total Win container mounts
  const setFRTotalRef = useCallback(
    (node) => {
      frTotalContainerRef.current = node;
      if (node) {
        slideYRef.current.y = frTotalYWhenAlone;
        node.y = frTotalYWhenAlone;
        node.alpha = 0; // start hidden, fade effect handles showing
      }
    },
    [frTotalYWhenAlone],
  );

  // The number the Win row prints. Above the early return so the trace below
  // reads the same value the render does.
  const displayWin = isPCMode
    ? pcTotalWin
    : fsHoldAndWinCounted
      ? holdAndWinPaid
    : isBonusTriggerWinMode
      ? triggerWinRef.current
      : fsHoldAndWinWin
        ? holdAndWinCompletedTotalWin
      : fsWinInCampaign
        ? freeSpinsDisplayedTotalWin
      : hnwWinInCampaign
        ? holdAndWinCompletedTotalWin
        : activeRoundWin;

  // ── What the win display is SHOWING, whenever that changes ──
  //
  // None of this is stored anywhere — the rows are derived on every render from
  // four features' flags — so the Redux money trace cannot see it. One line on
  // change, in the same `[$]` channel, so a total that looks wrong on screen
  // can be lined up against the action that moved the number underneath.
  // Before the early return: hooks must run on every render.
  const displayMode = isPCMode
    ? "PC"
    : isFSCompletedMode
      ? "FS-done"
      : isFreeSpinsMode
        ? "FS"
        : isFRCompletedMode
          ? "FR-done"
          : isFreeRoundsMode
            ? "FR"
            : "BASE";
  const displaySummary =
    `mode=${displayMode}` +
    // Same rule as the row itself — winTextShouldShow = isPCMode || isWinMode.
    ` win=${isWinMode || isPCMode ? displayWin : "hidden"}` +
    ` total=${hasTotalWinRow ? totalWinRowValue : "hidden"}` +
    (isFreeRoundsMode ? ` rounds=${freeRoundsPerformed}/${freeRoundsCount}` : "");
  useEffect(() => {
    moneyDiag("winDisplay", displaySummary);
  }, [displaySummary]);

  // Nothing to show — no win, no PC total win, no FR completed win, no free rounds counter
  if (
    !isPCMode &&
    !isFRCompletedMode &&
    !isWinMode &&
    !isFreeRoundsMode &&
    !hasTotalWinRow
  ) {
    return null;
  }

  // ── Text content ──────────────────────────────────────────────────────

  // Win line (current spin win, PC total win, FR completed total win, bonus win during FR, or trigger win during bonus)
  // const winLabelText = (isPCMode || isFRCompletedMode || isBonusTriggerWinMode)
  const winLabelText = (isPCMode || isFRCompletedMode || isBonusTriggerWinMode || fsHoldAndWinCounted)
    ? `${t("common:total_win")}: `
    : `${t("common:win")}: `;
  const winValueText = `${formatToCustomLocale(displayWin)} ${currency}`;

  // FR total win line
  const frTotalLabelText = `${t("common:total_win")}: `;
  const frTotalValueText = `${formatToCustomLocale(totalWinRowValue)} ${currency}`;

  // FR counter line
  const frCounterLabelText = isFreeRoundsMode ? `${t("common:free_rounds")}: ` : "";
  const frCounterValueText = isFreeRoundsMode ? `${freeRoundsPerformed}/${freeRoundsCount}` : "";

  // ── Text metrics ──────────────────────────────────────────────────────
  // During free rounds, always measure ALL rows (even if not yet visible)
  // so that colonX stays stable and positions don't shift when lines appear/disappear.
  const winLabelMetrics = PIXI.TextMetrics.measureText(winLabelText, winLabelStyle);
  const winValueMetrics = PIXI.TextMetrics.measureText(winValueText, winStyle);

  const showTotalWinRow =
    isFreeRoundsMode || isFRCompletedMode || isFreeSpinsMode || isFSCompletedMode;
  const frTotalLabelMetrics = showTotalWinRow
    ? PIXI.TextMetrics.measureText(frTotalLabelText, winLabelStyle)
    : null;
  const frTotalValueMetrics = showTotalWinRow
    ? PIXI.TextMetrics.measureText(frTotalValueText, winStyle)
    : null;

  const frCounterLabelMetrics = isFreeRoundsMode
    ? PIXI.TextMetrics.measureText(frCounterLabelText, frLabelStyle)
    : null;
  const frCounterValueMetrics = isFreeRoundsMode
    ? PIXI.TextMetrics.measureText(frCounterValueText, winStyle)
    : null;

  // ── Two-column layout: labels right-aligned, values left-aligned ──────
  // In free rounds mode, always include all three rows so layout is stable.
  const labelWidths = [];
  const valueWidths = [];

  // Win row — always included during free rounds (even when not showing)
  if (
    isPCMode ||
    isFRCompletedMode ||
    isWinMode ||
    isFreeRoundsMode ||
    isFreeSpinsMode ||
    isFSCompletedMode
  ) {
    labelWidths.push(winLabelMetrics.width);
    valueWidths.push(winValueMetrics.width);
  }
  // Total Win row — included during free rounds AND after FR completion
  if (showTotalWinRow && frTotalLabelMetrics) {
    labelWidths.push(frTotalLabelMetrics.width);
    valueWidths.push(frTotalValueMetrics.width);
  }
  // FR counter row
  if (isFreeRoundsMode && frCounterLabelMetrics) {
    labelWidths.push(frCounterLabelMetrics.width);
    valueWidths.push(frCounterValueMetrics.width);
  }

  const maxLabelWidth = Math.max(...labelWidths, 0);
  const maxValueWidth = Math.max(...valueWidths, 0);
  const totalBlockWidth = maxLabelWidth + maxValueWidth;
  const colonX = -totalBlockWidth / 2 + maxLabelWidth;

  // ── Fixed Y positions ─────────────────────────────────────────────────
  // Win text is always at y=0 (center of the display area)
  // FR counter is always at y=lineHeight (below center)
  // Total Win is animated via ref (see useEffect above)
  const winY = 0;
  const frCounterY = lineHeight;

  // ── Available space awareness for portrait modes ──────────────────────
  let winScale = 1;
  // ── The REAL gap (2026-10-15) ── the balance and bet report where their
  // text ends (toolbarEdges); the win centres in that gap, 10 px clear of
  // each side, and shrinks only if it still does not fit. The old fit below
  // measured the widest possible balance and the toolbar's middle, and the
  // win ran under a long balance ("814.6410 mBTC").
  let winX = winConfig.x;
  const { balanceRight, betLeft } = edges;
  if (isPortrait && balanceRight != null && betLeft != null && betLeft > balanceRight) {
    const free = betLeft - balanceRight - 2 * PORTRAIT_WIN_CLEARANCE;
    winX = (balanceRight + betLeft) / 2;
    if (totalBlockWidth > free) winScale = Math.max(free, 1) / totalBlockWidth;
  } else if (isPortrait && toolbar.layout) {
    const balanceLayout = toolbar.layout.balance;
    const betLayout = toolbar.layout.bet;
    if (balanceLayout && betLayout) {
      const balanceRight = balanceLayout.x + (balanceLayout.width || 0);
      const betLeft = betLayout.x - (betLayout.width || 0);
      const winCenterX = toolbar.width / 2;
      const spaceLeft = winCenterX - balanceRight;
      const spaceRight = betLeft - winCenterX;
      const padding = 20;
      // Use the smaller side so centered text never overflows either edge
      const availableHalf = Math.max(Math.min(spaceLeft, spaceRight) - padding, 25);
      const maxWidth = availableHalf * 2;
      if (totalBlockWidth > maxWidth) {
        winScale = maxWidth / totalBlockWidth;
      }
    }
  }



  return (
    <Container
      name="secondary_win_display"
      // x={winConfig.x}
      x={winX}
      y={winConfig.y}
      zIndex={4}
      scale={winScale}
    >
      {/* ── FR Total Win line (always rendered, alpha + position via refs) ── */}
      <Container ref={setFRTotalRef}>
        <Text
          text={frTotalLabelText}
          style={winLabelStyle}
          anchor={{ x: 1, y: 0.5 }}
          roundPixels={true}
          x={colonX}
        />
        <Text
          text={frTotalValueText}
          style={winStyle}
          anchor={{ x: 0, y: 0.5 }}
          roundPixels={true}
          x={colonX}
        />
      </Container>

      {/* ── Win text (always rendered, alpha managed via ref) ── */}
      <Container ref={setWinTextRef} y={winY}>
        <Text
          text={winLabelText}
          style={winLabelStyle}
          anchor={{ x: 1, y: 0.5 }}
          roundPixels={true}
          x={colonX}
        />
        <Text
          text={winValueText}
          style={winStyle}
          anchor={{ x: 0, y: 0.5 }}
          roundPixels={true}
          x={colonX}
        />
      </Container>

      {/* ── Free Rounds counter (always rendered, alpha via ref) ── */}
      <Container ref={setFRCounterRef} y={frCounterY}>
        <Text
          text={frCounterLabelText}
          style={frLabelStyle}
          anchor={{ x: 1, y: 0.5 }}
          roundPixels={true}
          x={colonX}
        />
        <Text
          text={frCounterValueText}
          style={winStyle}
          anchor={{ x: 0, y: 0.5 }}
          roundPixels={true}
          x={colonX}
        />
      </Container>
    </Container>
  );
};

export default WinDisplay;
